/**
 * SUB-4.3 · FR36. What "status" has to mean before it can be offered as a filter
 * across accounts.
 *
 * ⚠️ FR36 LISTS STATUS AS CONFIGURATION-INDEPENDENT. IT WAS NOT.
 * `projects.project_status` is `text` with no constraint, and the live data on
 * 2026-08-13 carried NINE spellings across NINE accounts, five of them in
 * exactly one account:
 *
 *   active 1,265 | Active 968 | Deleted 214 | Hold 35 | Cancelled 17 | Complete 2   (Solerro)
 *   Active 767   | active 239 | Deleted 12  | Cancelled 1 | Hold 1                  (Quality Solar)
 *   active 402   | cancelled 295 | completed 44 | Active 11 | on_hold 8              (Southeast Energy)
 *
 * So `.eq('project_status', 'Active')` — which is what the Projects list did, and
 * what every `?status=` deep link still passes — returns 767 of Quality Solar's
 * 1,006 active projects and 968 of Solerro's 2,233. In a merged list over both,
 * it drops most of one account and most of the other, for a reason no one reading
 * the screen could infer. That is precisely the failure FR36 exists to prevent,
 * and it was hiding on the one filter FR36 says IS portable.
 *
 * This module is the fix: the filter's unit is a CANONICAL STATUS, and the query
 * matches every spelling that folds into it, case-insensitively, on the server.
 * The bound stays where ISO4 puts it; only the comparison changed.
 *
 * ⚠️ THE FIX WIDENS THE SINGLE-ACCOUNT FILTER TOO, and that is deliberate. A
 * Solerro user who followed `?status=Active` was already missing 1,265 rows that
 * are 'active'. Those rows are active. Recorded under SUB-4.3 in
 * `security-findings.md`; the divergence itself is pre-existing and larger than
 * this filter.
 */

export interface CanonicalStatus {
  /** The bucket id, and what the filter state stores. */
  id: string;
  /** What the option is called. */
  label: string;
  /**
   * Every spelling that folds into this bucket, one entry per distinct SPELLING.
   * Casing variants do not need listing: the match is case-insensitive.
   */
  spellings: string[];
}

/**
 * The five buckets the nine live spellings fold into.
 *
 * `spellings` carries synonyms that normalisation alone would not join
 * ('completed' is not 'complete' after case folding, 'hold' is not 'on_hold').
 * Anything not listed here still gets a bucket of its own from
 * `canonicalProjectStatus`, so an account inventing a status is under-served
 * rather than mis-served.
 */
export const CANONICAL_PROJECT_STATUSES: readonly CanonicalStatus[] = [
  { id: 'active', label: 'Active', spellings: ['active'] },
  { id: 'on_hold', label: 'On hold', spellings: ['hold', 'on_hold', 'on hold', 'on-hold'] },
  { id: 'complete', label: 'Complete', spellings: ['complete', 'completed'] },
  { id: 'cancelled', label: 'Cancelled', spellings: ['cancelled', 'canceled'] },
  { id: 'deleted', label: 'Deleted', spellings: ['deleted'] },
];

/** Case-fold, collapse every run of non-alphanumerics to one underscore, trim. */
function normalise(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Synonyms that survive normalisation as different strings. */
const SYNONYMS: Record<string, string> = {
  hold: 'on_hold',
  onhold: 'on_hold',
  completed: 'complete',
  canceled: 'cancelled',
  cancel: 'cancelled',
  archived: 'deleted',
};

/**
 * The bucket a stored value belongs to, or null when it carries no status.
 *
 * ⚠️ AN UNRECOGNISED VALUE KEEPS ITS OWN BUCKET. Folding 'Permitting' into
 * 'active' would make the filter lie; giving it a bucket of its own makes the
 * option list longer and every count honest.
 */
export function canonicalProjectStatus(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const normalised = normalise(raw);
  if (!normalised) return null;

  const known = CANONICAL_PROJECT_STATUSES.find((status) =>
    status.spellings.some((spelling) => normalise(spelling) === normalised),
  );
  if (known) return known.id;

  return SYNONYMS[normalised] ?? normalised;
}

/**
 * Every spelling to match for a bucket.
 *
 * ⚠️ NEVER RETURNS AN EMPTY LIST. An empty list would build a filter that
 * matches nothing, which renders as "this account has no active projects".
 */
export function spellingsForCanonicalStatus(id: string): string[] {
  const known = CANONICAL_PROJECT_STATUSES.find((status) => status.id === id);
  if (known) return [...known.spellings];
  return [id];
}

export interface ProjectStatusOption {
  id: string;
  label: string;
  /** The spellings this option matches, so the panel can show them on hover. */
  spellings: string[];
  /** False when the bucket came from the data rather than from the vocabulary. */
  known: boolean;
}

/**
 * The options a status filter offers.
 *
 * The canonical vocabulary always, plus any bucket present in the values the
 * SERVER returned that the vocabulary does not know. Deriving the extras from
 * fetched rows is safe in a way a bound would not be: an option list is not a
 * predicate, and its only failure mode is hiding a status that exists.
 */
export function projectStatusOptions(
  observed: readonly (string | null | undefined)[],
): ProjectStatusOption[] {
  const options: ProjectStatusOption[] = CANONICAL_PROJECT_STATUSES.map((status) => ({
    id: status.id,
    label: status.label,
    spellings: [...status.spellings],
    known: true,
  }));

  const seen = new Set(options.map((option) => option.id));

  for (const raw of observed) {
    const id = canonicalProjectStatus(raw);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    options.push({
      id,
      // The value as the account spells it, so an unmapped status is recognisable
      // rather than rendered as a normalised slug.
      label: (raw as string).trim(),
      spellings: [(raw as string).trim()],
      known: false,
    });
  }

  return options;
}
